import type { Placement } from "@floating-ui/vue";
import type { PropType } from "vue";

import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/vue";
import {
  useEventListener,
  useFocusWithin,
  useMouseInElement,
} from "@vueuse/core";
import { isNumber } from "radash";
import {
  computed,
  nextTick,
  ref,
  toRef,
  useSlots,
  watch,
  watchEffect,
} from "vue";

import { propsDefinition } from "@/lib/composables/componentComposable";
import { useTemplateRef } from "@/lib/composables/useTemplateRef";

type UseTooltipScoped = {
  disabled?: boolean;
  tooltip?: string;
};

type UseTooltipProps = UseTooltipScoped & {
  anchorEl?: HTMLElement;
  dark?: boolean;
  fallbackPlacements?: Placement[];
  isOpen?: boolean | null;
  offset?: number;
  openOnHover?: boolean;
  padding?: number;
  placement?: Placement;
  teleportToBody?: boolean;
  tooltipId?: string;
  triggerEl?: HTMLElement;
};

const scoped = propsDefinition({
  disabled: { type: Boolean, default: false },
  tooltip: { type: String, required: false },
}) satisfies Record<keyof UseTooltipScoped, unknown>;

const props = propsDefinition({
  ...scoped,
  anchorEl: { type: Object as PropType<HTMLElement>, required: false },
  dark: { type: Boolean, default: true },
  fallbackPlacements: {
    type: Array as PropType<Placement[]>,
    default: () => ["top", "right", "bottom", "left"],
  },
  isOpen: { type: Boolean, default: null },
  offset: { type: Number, default: 8 },
  openOnHover: { type: Boolean, default: true },
  padding: { type: Number, default: 12 },
  placement: { type: String as PropType<Placement>, default: "top" },
  teleportToBody: { type: Boolean, default: false },
  tooltipId: { type: String, required: false },
  triggerEl: { type: Object as PropType<HTMLElement>, required: false },
}) satisfies Record<keyof UseTooltipProps, unknown>;

function use(props: UseTooltipProps) {
  const disabled = computed(() => {
    return props.disabled || (!props.tooltip && !useSlots().content);
  });

  const { el: autoTriggerEl, ref: triggerRef } = useTemplateRef();
  const { el: popupEl, ref: popupRef } = useTemplateRef();
  const { el: arrowEl, ref: arrowRef } = useTemplateRef();

  const triggerEl = computed(() => {
    return props.triggerEl ?? autoTriggerEl.value;
  });

  const anchorEl = computed<HTMLElement | null>(() => {
    return props.anchorEl ?? triggerEl.value;
  });

  const { isOutside } = useMouseInElement(triggerEl);
  const { focused } = useFocusWithin(triggerEl);

  const isTriggered = computed(() => {
    return (!isOutside.value && props.openOnHover) || focused.value;
  });

  const isDismissed = ref(false);
  useEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key === "Escape" && isTriggered.value) {
      isDismissed.value = true;
    }
  });
  watch(isTriggered, (triggered) => {
    if (!triggered) {
      isDismissed.value = false;
    }
  });

  const isOpen = computed(() => {
    return props.isOpen ?? (isTriggered.value && !isDismissed.value);
  });

  const { floatingStyles, middlewareData, placement, update } = useFloating(
    anchorEl,
    popupEl,
    {
      placement: toRef(() => props.placement),
      whileElementsMounted: (...args) =>
        autoUpdate(...args, { animationFrame: false }),
      middleware: computed(() => [
        offset(props.offset),
        flip({
          fallbackPlacements: props.fallbackPlacements
            ? [...props.fallbackPlacements]
            : [],
          crossAxis: false,
          padding: props.padding,
        }),
        shift({ padding: props.padding }),
        arrow({ element: arrowEl }),
      ]),
    },
  );

  watch(isOpen, async (open) => {
    if (open) {
      await nextTick();
      update();
    }
  });

  watchEffect(() => {
    if (popupEl.value) {
      Object.assign(popupEl.value.style, floatingStyles.value);
    }
  });

  watchEffect(() => {
    if (!middlewareData.value.arrow || !arrowEl.value) {
      return;
    }

    const { x: left, y: top } = middlewareData.value.arrow;

    const staticSide = {
      top: "bottom",
      right: "left",
      bottom: "top",
      left: "right",
    }[placement.value.split("-")[0]!]!;

    Object.assign(arrowEl.value.style, {
      left: isNumber(left) ? `${left}px` : "",
      top: isNumber(top) ? `${top}px` : "",
      [staticSide]: `${-arrowEl.value.offsetWidth / 2}px`,
    });
  });

  return {
    triggerRef,
    popupRef,
    arrowRef,
    isOpen,
    tooltip: toRef(() => props.tooltip),
    dark: toRef(() => props.dark),
    teleportToBody: toRef(() => props.teleportToBody),
    disabled,
    id: toRef(() => props.tooltipId),
  };
}

export type { UseTooltipProps, UseTooltipScoped };
export { props, scoped, use };
